import {
  signalStore,
  withState,
  withComputed,
  withMethods,
  patchState,
} from '@ngrx/signals';
import { CustomGridStackWidget } from '../../services/grid-widget-config.service';
import { computed, inject } from '@angular/core';
import { Bookmark, LayoutOptions } from '../../models/bookmark';
import { BookmarkStore } from '../bookmark/bookmark.state';
import { GlobalsStore } from '../globals.state';

interface GridState {
  activeBookmark: Bookmark | undefined;
  bookmarkToEdit: Bookmark | undefined;
  showBookmarkForm: boolean;
  items: CustomGridStackWidget[];
  layout: CustomGridStackWidget[];
  options: LayoutOptions | null;
  overwriteLayout: boolean;
  readonly: null | boolean;
  gridHasChanges: boolean;
  /**
   * While true, `triggerLayoutChanges()` is ignored - set while a layout is
   * being (re)built so programmatic widget adds/updates don't count as user
   * changes. Starts suspended until the grid has initialized.
   */
  changeTrackingSuspended: boolean;
  arrangeDialogOpen: boolean;
}

const initialGridState: GridState = {
  activeBookmark: undefined,
  bookmarkToEdit: undefined,
  showBookmarkForm: false,
  items: [],
  layout: [],
  options: null,
  overwriteLayout: false,
  readonly: null,
  gridHasChanges: false,
  changeTrackingSuspended: true,
  arrangeDialogOpen: false,
};

export const GridStore = signalStore(
  { providedIn: 'root' },
  withState(initialGridState),
  withComputed(({ activeBookmark }, globalsStore = inject(GlobalsStore)) => ({
    /**
     * Whether nothing is currently active - no topic and no bookmark -
     * i.e. the blank "Werkstatt" workspace reached via "Erstellen" on the
     * landing page.
     */
    isWorkshopMode: computed(
      () => !globalsStore.activeTopic() && !activeBookmark()
    ),
  })),
  withMethods((store, bookmarkStore = inject(BookmarkStore)) => ({
    /**
     * Resets the grid state (called on every atlas route change). The change
     * tracking gate is owned by the grid component's loading cycle and
     * therefore kept - otherwise tracking would stay off after navigating.
     */
    reset() {
      patchState(store, {
        ...initialGridState,
        changeTrackingSuspended: store.changeTrackingSuspended(),
      });
    },
    suspendChangeTracking(): void {
      patchState(store, { changeTrackingSuspended: true });
    },
    /**
     * Re-enables change tracking. By default the current grid becomes the
     * clean baseline; pass `markAsChanged` if the just loaded layout itself is
     * an unsaved change (generated from a template, rearranged, …).
     */
    resumeChangeTracking(markAsChanged = false): void {
      patchState(store, {
        changeTrackingSuspended: false,
        gridHasChanges: markAsChanged,
      });
    },
    setActiveBookmarkById(bookmarkId: string, forceBookmark?: Bookmark): void {
      const bookmarkToActivate =
        forceBookmark || bookmarkStore.getByIdOrToken(bookmarkId);

      if (bookmarkToActivate && bookmarkToActivate.config) {
        patchState(store, {
          activeBookmark: bookmarkToActivate,
          layout: structuredClone(bookmarkToActivate.config),
          options: bookmarkToActivate.layoutOptions,
          overwriteLayout: true,
          gridHasChanges: false,
        });
        setTimeout(() => patchState(store, { overwriteLayout: false }));
      } else {
        patchState(store, {
          activeBookmark: undefined,
          layout: [],
          options: null,
          overwriteLayout: true,
          gridHasChanges: false,
        });
        setTimeout(() => patchState(store, { overwriteLayout: false }));
      }
    },
    unsetActiveBookmark(): void {
      patchState(store, {
        activeBookmark: undefined,
        readonly: null,
        layout: [],
      });
    },
    saveLayout(layout: CustomGridStackWidget[]): void {
      patchState(store, { layout });
    },
    resetGridHasChanges(): void {
      patchState(store, { gridHasChanges: false });
    },
    loadLayout(layout: CustomGridStackWidget[], options: LayoutOptions) {
      patchState(store, { layout, overwriteLayout: true, options });
      setTimeout(() => patchState(store, { overwriteLayout: false }));
    },
    markFeature(
      featureKey: string,
      type: 'feature' | 'indicator' | 'dataLayer' = 'feature'
    ): void {
      const options: LayoutOptions = store.options() || {
        selectedFeatures: [],
        highlightedFeature: undefined,
        activeTopic: null,
      };

      const currentFeatures = [...(options.selectedFeatures || [])];
      const existingIndex = currentFeatures.findIndex(
        item => item.key === featureKey && item.type === type
      );

      if (existingIndex !== -1) {
        // Remove if already selected
        currentFeatures.splice(existingIndex, 1);
      } else {
        // Add new selection
        currentFeatures.push({ key: featureKey, type });
      }

      patchState(store, {
        options: { ...options, selectedFeatures: currentFeatures },
      });
    },
    highlightFeature(featureKey?: string, type?: string): void {
      setTimeout(() => {
        const options: LayoutOptions = store.options() || {
          selectedFeatures: [],
          highlightedFeature: undefined,
          activeTopic: null,
        };
        patchState(store, {
          options: {
            ...options,
            highlightedFeature:
              featureKey && type ? { key: featureKey, type: type } : undefined,
          },
        });
      }, 25);
    },
    layout(): CustomGridStackWidget[] {
      return store.layout();
    },
    openBookmarkForm(bookmark?: Bookmark): void {
      patchState(store, { showBookmarkForm: true, bookmarkToEdit: bookmark });
    },
    closeBookmarkForm() {
      patchState(store, { showBookmarkForm: false, bookmarkToEdit: undefined });
    },
    triggerLayoutChanges(): void {
      // Checked again in the microtask: a load may have started in between.
      if (store.changeTrackingSuspended()) return;
      queueMicrotask(() => {
        if (!store.changeTrackingSuspended()) {
          patchState(store, { gridHasChanges: true });
        }
      });
    },
    setReadonly(state: boolean): void {
      patchState(store, { readonly: state });
      if (state) {
        patchState(store, { arrangeDialogOpen: false });
      }
    },
    openArrangeDialog(): void {
      patchState(store, { arrangeDialogOpen: true });
    },
    closeArrangeDialog(): void {
      patchState(store, { arrangeDialogOpen: false });
    },
  }))
);
